import React, { ReactNode, useState, MutableRefObject, ElementType } from 'react';

import { Collapse } from '../Collapse';

import {
  Accordion,
  AccordionBody,
  AccordionButton,
  AccordionSection,
  AccordionSectionHeading,
  AccordionSectionHeadingContainer,
  AccordionV2Container,
  StyledCaret,
  Title,
  TitleContainer,
} from './AccordionV2.styles';

type SelectedItemsType = (string | number)[];

export type AccordionItemType = {
  dek: string | ReactNode;
  hed: string | ReactNode;
  id: string | number;
};

export type AccordionV2Type = {
  allowMultiple?: boolean;
  arrowOnLeft?: boolean;
  className?: string;
  collapsed?: boolean;
  initialExpandedItems?: number | number[] | string | string[];
  itemButtonRefs?: MutableRefObject<{ [key: string]: HTMLButtonElement | null }>;
  items: AccordionItemType[];
  onClickCb?: () => void;
  title?: string | ReactNode;
  trackClicked?: (item: AccordionItemType) => void;

  /**
   * Override the default H3 semantic element of the accordion header with a different element type
   *
   * @example
   * // Generate an accordion that uses a div instead of an h3 for the headers with each item
   * <AccordionV2
   *   accordionHeaderComponent="div"
   * />
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  accordionHeaderComponent?: ElementType<any>;
};

const AccordionV2: React.FC<AccordionV2Type> = ({
  allowMultiple = false,
  arrowOnLeft = true,
  className,
  collapsed = true,
  initialExpandedItems = undefined,
  itemButtonRefs,
  items,
  onClickCb,
  title,
  trackClicked,
  accordionHeaderComponent,
}) => {
  const [expandedSections, setExpandedSections] = useState<SelectedItemsType>(
    (() => {
      if (collapsed === false) {
        return items.map((item) => item.id);
      }
      return ([initialExpandedItems] || []).flatMap((item) => item) as SelectedItemsType;
    })()
  );

  const toggleItem = (item: AccordionItemType): void => {
    const { id } = item;
    const isOpen = expandedSections.indexOf(id) >= 0;
    if (trackClicked && !isOpen) {
      trackClicked(item);
    }
    if (onClickCb) {
      onClickCb();
    }
    let updatedItems: SelectedItemsType = [];
    if (allowMultiple) {
      updatedItems = isOpen ? expandedSections.filter((i) => i !== id) : [...expandedSections, id];
    } else if (!isOpen) {
      updatedItems = [id];
    }

    setExpandedSections(updatedItems);
  };

  const renderItems = (): JSX.Element[] | undefined => {
    if (items && items.length > 0) {
      return items.map((item) => {
        const isOpen = expandedSections.indexOf(item.id) >= 0;
        return (
          <AccordionSection isOpen={isOpen} key={item.id}>
            <AccordionSectionHeadingContainer>
              <AccordionButton
                arrowOnLeft={arrowOnLeft}
                onClick={() => toggleItem(item)}
                type="button"
                ref={(ref) => {
                  if (!itemButtonRefs) return;
                  // eslint-disable-next-line no-param-reassign
                  itemButtonRefs.current[item.id] = ref;
                }}
                aria-controls={`Collapse--${item.id}`}
                aria-expanded={isOpen}
              >
                <AccordionSectionHeading as={accordionHeaderComponent}>
                  {item.hed}
                </AccordionSectionHeading>
                <StyledCaret
                  arrowOnLeft={arrowOnLeft}
                  direction={isOpen ? 'up' : 'down'}
                  aria-hidden="true"
                />
              </AccordionButton>
            </AccordionSectionHeadingContainer>
            <Collapse isOpen={isOpen} id={`Collapse--${item.id}`}>
              <AccordionBody>{item.dek}</AccordionBody>
            </Collapse>
          </AccordionSection>
        );
      });
    }
    return undefined;
  };

  return (
    <AccordionV2Container className={className}>
      {title && (
        <TitleContainer>
          <Title>{title}</Title>
        </TitleContainer>
      )}
      <Accordion>{renderItems()}</Accordion>
    </AccordionV2Container>
  );
};

export default AccordionV2;
